import {
	ReactElement,
	ReadonlyRefObject,
	Ref,
	useCallback,
	useImperativeHandle,
	useMemo,
	useRef,
	useState
} from "react";
import classNames from "classnames";
import { CalendarCell } from "./CalendarCell";
import styles from "./MonthView.module.less";
import {
	Week
} from "./Week";
import { WeekDays } from "./WeekDays";
import { DateOnly } from "client/DateAndTime/dateOnly";
import {
	addDays,
	daysInAWeek,
	generateDateArray,
	getStartOfWeek,
	getWeekNumber,
	isBetween
} from "client/DateAndTime/dateUtils";
import {
	CalendarHighlightDateOnlyRange,
	CalendarNavigationKey,
	isCalendarNavigationKey
} from "./Calendar";
import { NormalizedMap } from "client_legacy/ndaw/normalizedMap";
import { generateTrackingId, isNil } from "client/Misc/utils";
import { KeyEventArguments } from "../TextInput/TextInput";
import { Key } from "client/Misc/commonTypes";

export type WeekViewHandle = {
	focusDate(date: DateOnly): void;
};

export type WeekViewProps = {
	firstWeekDay: number;
	viewDate: DateOnly;
	today: DateOnly;
	highlightRange: CalendarHighlightDateOnlyRange | null;
	onSelect(value: DateOnly): void;
	showWeekNumbers: boolean | undefined;
	eventDateMap: Map<DateOnly, boolean>;
	calculateHighlightRangeForDate(date: DateOnly): CalendarHighlightDateOnlyRange;
	onViewRangeChange: (modifier: 1 | -1) => void;
	minDate: DateOnly | null;
	maxDate: DateOnly | null;
	ref: Ref<WeekViewHandle>;
};

export const WeekView = (props: WeekViewProps): ReactElement => {
	const {
		firstWeekDay,
		viewDate,
		today,
		highlightRange,
		onSelect,
		showWeekNumbers = false,
		eventDateMap,
		calculateHighlightRangeForDate,
		onViewRangeChange,
		minDate,
		maxDate,
		ref
	} = props;

	const [hoveredDate, setHoveredDate] = useState<DateOnly | null>(null);
	const [focusableDate, setFocusableDate] = useState<DateOnly | undefined>(undefined);

	const itemRefs: ReadonlyRefObject<
		NormalizedMap<DateOnly, HTMLElement | null>
	> = useRef<NormalizedMap<DateOnly, HTMLElement | null>>(
		new NormalizedMap<DateOnly, HTMLElement | null>(x => x.toISOString())
	);

	const hoverRange = useMemo(
		(): CalendarHighlightDateOnlyRange | null => {
			if (!hoveredDate) {
				return null;
			}

			return calculateHighlightRangeForDate(hoveredDate);
		},
		[calculateHighlightRangeForDate, hoveredDate]
	);

	const dates: readonly DateOnly[] = generateDateArray(getStartOfWeek(viewDate), daysInAWeek);

	const isDateInView = useCallback(
		(date: DateOnly): boolean => {
			return isBetween(
				date,
				dates[0],
				addDays(
					dates[dates.length - 1],
					1
				)
			);
		},
		[dates]
	);

	const computedFocusableDate = useMemo(
		(): DateOnly => {
			if (isNil(focusableDate) || !isDateInView(focusableDate)) {
				return dates[0];
			}

			return focusableDate;
		},
		[
			dates,
			focusableDate,
			isDateInView
		]
	);

	const setItemRef = useCallback(
		(item: HTMLElement | null, date: DateOnly): void => {
			if (isNil(item)) {
				return;
			}

			itemRefs.current
				.set(date, item);
		},
		[]
	);

	const focusItemHandler = useCallback(
		(targetMonth: DateOnly): void => {
			setFocusableDate(targetMonth);

			// The setTimeout is used to delay focusing the target month until after the view range has changed
			setTimeout(
				(): void => {
					itemRefs.current
						?.get(targetMonth)
						?.focus();
				},
				1
			);
		},
		[]
	);

	const movementHandler = (args: KeyEventArguments, selectedDate: DateOnly): boolean => {
		if (isNil(args.key) || !isCalendarNavigationKey(args.key)) {
			return false;
		}

		const keyToOffset: Record<CalendarNavigationKey, number> = {
			[Key.ArrowDown]: daysInAWeek,
			[Key.ArrowUp]: -daysInAWeek,
			[Key.ArrowLeft]: -1,
			[Key.ArrowRight]: 1
		};

		const offset: number = keyToOffset[args.key];
		const targetDate: DateOnly = addDays(selectedDate, offset);

		if (!isDateInView(targetDate)) {
			onViewRangeChange(offset > 0 ? 1 : -1);
		}

		focusItemHandler(targetDate);

		return true;
	};

	useImperativeHandle(
		ref,
		(): WeekViewHandle => ({
			focusDate: (date: DateOnly): void => {
				focusItemHandler(date);
			}
		}),
		[focusItemHandler]
	);

	return (
		<div
			className={classNames(
				styles.root,
				{
					[styles.root_withWeekNumbers]: showWeekNumbers,
				}
			)}
			role="grid"
		>
			{showWeekNumbers && (
				<CalendarCell
					trackingId="week-number-header"
					kind="header"
				/>
			)}

			<WeekDays firstWeekDay={firstWeekDay} />

			{showWeekNumbers && (
				<CalendarCell
					trackingId={generateTrackingId("week-number", `|${getWeekNumber(viewDate)}`)}
					kind="week-number"
				>
					{getWeekNumber(viewDate)}
				</CalendarCell>
			)}
			<Week
				dates={dates}
				highlightRange={highlightRange}
				hoverRange={hoverRange}
				onHover={setHoveredDate}
				onSelect={onSelect}
				today={today}
				eventDateMap={eventDateMap}
				minDate={minDate}
				maxDate={maxDate}
				focusableDate={computedFocusableDate}
				setItemRef={setItemRef}
				onMovement={movementHandler}
			/>
		</div>
	);
};

WeekView.displayName = "WeekView";
